import React, {useEffect, useMemo, useRef, useState} from 'react';
import Forms from "./Forms";
import Error from "./Error";
import API from "../utils/API";
import {API_ENDPOINTS} from "../apiConfig";
import PhotoGallery from "./PhotoGallery";

const SALE_STATUS_OPTIONS = [
    {value: 'listed', label: 'Vystaveno k prodeji'},
    {value: 'sold', label: 'Prodáno'}
];

const ITEM_TYPE_OPTIONS = [
    {value: 'device', label: 'Zařízení'},
    {value: 'sparePart', label: 'Náhradní díl'}
];

const DIAG_STATUS_OPTIONS = [
    {value: 'untested', label: 'Netestováno'},
    {value: 'functional', label: 'Funkční'},
    {value: 'nonfunctional', label: 'Nefunkční'}
];

const DIAG_GRADE_OPTIONS = [
    {value: 'A', label: 'A'},
    {value: 'B', label: 'B'},
    {value: 'C', label: 'C'}
];

const EMPTY_FORM = {
    itemType: 'device',
    device: null,
    part: null,
    status: 'listed',
    listedPrice: '',
    salePrice: '',
    saleDate: '',
    buyer: {name: '', surname: '', email: '', phone: ''},
    note: '',
    diagnostics: []
};

const SaleModal = ({close, sale, initialBuyback = null, onSaved}) => {
    const isCreate = !sale?._id && !!initialBuyback?._id;
    const [form, setForm] = useState(EMPTY_FORM);
    const [diagnosticCatalog, setDiagnosticCatalog] = useState([]);
    const [linkedRepair, setLinkedRepair] = useState(null);
    const [repairSummaryLoading, setRepairSummaryLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [pendingPhotoFiles, setPendingPhotoFiles] = useState([]);
    const pendingRef = useRef([]);

    useEffect(() => {
        pendingRef.current = pendingPhotoFiles;
    }, [pendingPhotoFiles]);

    useEffect(() => () => {
        pendingRef.current.forEach((entry) => revokePreview(entry?.previewUrl));
    }, []);

    useEffect(() => {
        let active = true;
        const loadDiagnosticCatalog = async () => {
            try {
                const response = await API.get(API_ENDPOINTS.diagnosticCatalog.list);
                if (!active) return;
                setDiagnosticCatalog(Array.isArray(response?.data) ? response.data : []);
            } catch (err) {
                if (!active) return;
                setError(err);
            }
        };
        loadDiagnosticCatalog().catch(() => {});
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        const answerSource = sale?._id ? (sale?.diagnostics || []) : (initialBuyback?.diagnostics || []);
        const answerMap = new Map(
            (Array.isArray(answerSource) ? answerSource : [])
                .map((item) => [String(item?.item?._id || item?.item || ''), item])
                .filter(([id]) => Boolean(id))
        );
        const catalogMap = new Map(
            (Array.isArray(diagnosticCatalog) ? diagnosticCatalog : [])
                .map((item) => [String(item?._id || ''), item])
                .filter(([id]) => Boolean(id))
        );
        const buildDiagnosticItem = (diagnosticId, fallback = {}) => {
            const catalogItem = catalogMap.get(String(diagnosticId || '')) || {};
            const answer = answerMap.get(String(diagnosticId || '')) || fallback || {};
            const answerType = catalogItem?.answerType || answer?.answerType || 'status';
            const base = {
                item: diagnosticId,
                itemName: catalogItem?.name || answer?.itemName || answer?.item?.name || '',
                answerType
            };
            if (answerType === 'number') {
                return {...base, valueNumber: answer?.valueNumber ?? ''};
            }
            if (answerType === 'grade') {
                return {...base, valueGrade: answer?.valueGrade || 'C'};
            }
            return {...base, valueStatus: answer?.valueStatus || 'untested'};
        };

        if (sale?._id) {
            const diagnosticsFromSale = (sale.diagnostics || [])
                .map((item) => item.item?._id || item.item)
                .filter(Boolean)
                .map((diagnosticId) => buildDiagnosticItem(diagnosticId, answerMap.get(String(diagnosticId)) || {}));
            setForm({
                itemType: sale.itemType || 'device',
                device: sale.device?._id || sale.device || null,
                part: sale.part?._id || sale.part || null,
                status: sale.status || 'listed',
                listedPrice: sale.listedPrice ?? '',
                salePrice: sale.salePrice ?? '',
                saleDate: sale.saleDate || '',
                buyer: {
                    name: sale.buyer?.name || '',
                    surname: sale.buyer?.surname || '',
                    email: sale.buyer?.email || '',
                    phone: sale.buyer?.phone || ''
                },
                note: sale.note || '',
                diagnostics: diagnosticsFromSale
            });
            return;
        }

        const deviceId = initialBuyback?.device?._id || initialBuyback?.device || null;
        const deviceName = initialBuyback?.deviceSnapshot?.name || initialBuyback?.device?.name || '';
        const diagnostics = (initialBuyback?.device?.diagnosticItemList || [])
            .filter((item) => item?.active !== false && item?.item)
            .map((item) => item.item?._id || item.item)
            .filter(Boolean)
            .map((diagnosticId) => buildDiagnosticItem(diagnosticId, answerMap.get(String(diagnosticId)) || {}));

        setForm({
            ...EMPTY_FORM,
            itemType: 'device',
            device: deviceId,
            part: null,
            diagnostics,
            note: `Zdroj: výkup ${initialBuyback?.buybackNumber || ''}`.trim()
        });
        if (!deviceId && !deviceName) {
            setError({message: 'U výkupu chybí zařízení, nelze založit prodej.'});
        }
    }, [sale, initialBuyback, diagnosticCatalog]);

    useEffect(() => {
        let active = true;
        const loadLinkedRepair = async () => {
            if (!isCreate) return;
            const repairId = initialBuyback?.links?.repairId?._id || initialBuyback?.links?.repairId;
            if (!repairId) {
                if (active) setLinkedRepair(null);
                return;
            }
            setRepairSummaryLoading(true);
            try {
                const res = await API.get(API_ENDPOINTS.repairs.get(repairId));
                if (!active) return;
                setLinkedRepair(res?.data || null);
            } catch (_err) {
                if (!active) return;
                setLinkedRepair(null);
            } finally {
                if (active) setRepairSummaryLoading(false);
            }
        };
        loadLinkedRepair().catch(() => {});
        return () => {
            active = false;
        };
    }, [isCreate, initialBuyback?._id, initialBuyback?.links?.repairId]);

    const isSold = form.status === 'sold';

    const partOptions = useMemo(() => {
        const donorParts = initialBuyback?.item?.donorParts || [];
        const unique = new Map();
        donorParts.forEach((item) => {
            const partId = item?.part?._id || item?.part;
            const name = item?.part?.name || '';
            if (!partId) return;
            unique.set(String(partId), {value: partId, label: name || String(partId)});
        });
        return Array.from(unique.values());
    }, [initialBuyback?.item?.donorParts]);

    const currentPhotoAssets = useMemo(() => {
        const source = sale?.photoIds || initialBuyback?.item?.photoAssets || initialBuyback?.item?.photoIds || [];
        return source.filter((item) => item && typeof item === 'object');
    }, [sale?.photoIds, initialBuyback?.item?.photoAssets, initialBuyback?.item?.photoIds]);

    const currentPhotoItems = useMemo(() => currentPhotoAssets.map((item, index) => {
        const previewUrl = item?.variants?.webpThumb?.url || item?.variants?.webpFullHd?.url || item?.variants?.webpOriginal?.url || item?.original?.url || null;
        const fullUrl = item?.variants?.webpFullHd?.url || item?.variants?.webpOriginal?.url || item?.original?.url || previewUrl;
        const width = item?.variants?.webpFullHd?.width || item?.original?.width || item?.variants?.webpThumb?.width || null;
        const height = item?.variants?.webpFullHd?.height || item?.original?.height || item?.variants?.webpThumb?.height || null;
        return {
            id: item?._id || `photo_${index}`,
            previewUrl,
            fullUrl,
            width,
            height,
            name: item?.original?.path?.split('/')?.pop?.() || `Fotka ${index + 1}`,
            meta: width && height ? `${width}×${height}` : '',
            alt: `Fotka ${index + 1}`
        };
    }), [currentPhotoAssets]);

    const pendingPhotoItems = useMemo(() => (pendingPhotoFiles || []).map((entry, index) => ({
        id: entry.id || `pending_${index}`,
        previewUrl: entry.previewUrl,
        fullUrl: entry.previewUrl,
        width: entry.width,
        height: entry.height,
        name: entry.name,
        meta: [entry.dimensionsLabel, entry.sizeLabel].filter(Boolean).join(' • '),
        alt: `Nová fotka ${index + 1}`
    })), [pendingPhotoFiles]);

    const repairPartsSummary = useMemo(() => {
        const grouped = new Map();
        const deviceRepairs = linkedRepair?.repairs || [];
        deviceRepairs.forEach((deviceRepair) => {
            (deviceRepair?.repairList || []).forEach((task) => {
                (task?.partsUsed || []).forEach((usage) => {
                    const partId = usage?.part?._id || usage?.part;
                    if (!partId) return;
                    const key = String(partId);
                    const existing = grouped.get(key) || {
                        partId: key,
                        name: usage?.part?.name || key,
                        quantity: 0,
                        unitCost: getPartUnitCost(usage?.part),
                        totalCost: 0
                    };
                    existing.quantity += 1;
                    if (!existing.unitCost) {
                        existing.unitCost = getPartUnitCost(usage?.part);
                    }
                    existing.totalCost = existing.quantity * (existing.unitCost || 0);
                    grouped.set(key, existing);
                });
            });
        });
        return Array.from(grouped.values()).sort((a, b) => b.totalCost - a.totalCost);
    }, [linkedRepair]);

    const purchasePrice = Number(initialBuyback?.economics?.purchasePrice) || 0;
    const repairsPrice = useMemo(() => {
        const tasks = (linkedRepair?.repairs || []).flatMap((deviceRepair) => deviceRepair?.repairList || []);
        return tasks.reduce((sum, task) => {
            const basePrice = Number(task?.price) || 0;
            if (basePrice <= 0) return sum;
            const discountType = task?.discount?.type;
            const discountValue = Number(task?.discount?.value ?? 0);
            let discountAmount = 0;
            if (discountType === 'percent' && discountValue > 0) {
                discountAmount = basePrice * (discountValue / 100);
            } else if (discountType === 'fixed' && discountValue > 0) {
                discountAmount = discountValue;
            }
            return sum + Math.max(0, basePrice - discountAmount);
        }, 0);
    }, [linkedRepair]);
    const totalCosts = purchasePrice + repairsPrice;

    const setField = (path, value) => {
        setForm((prev) => {
            const next = {...prev};
            if (path.startsWith('buyer.')) {
                const key = path.slice(6);
                next.buyer = {...prev.buyer, [key]: value};
                return next;
            }
            next[path] = value;
            return next;
        });
    };

    const updateDiagnostic = (index, patch) => {
        setForm((prev) => ({
            ...prev,
            diagnostics: prev.diagnostics.map((item, i) => i === index ? {...item, ...patch} : item)
        }));
    };

    const removeStoredPhoto = async (photoId) => {
        if (!sale?._id || !photoId) return;
        try {
            await API.delete(API_ENDPOINTS.sales.deletePhoto(sale._id, photoId));
            await onSaved?.();
        } catch (err) {
            setError(err);
        }
    };

    const onSubmit = async (e) => {
        e?.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const payload = {
                itemType: form.itemType,
                device: form.itemType === 'device' ? form.device : null,
                part: form.itemType === 'sparePart' ? form.part : null,
                status: form.status || 'listed',
                listedPrice: toNumberOrNull(form.listedPrice),
                salePrice: isSold ? toNumberOrNull(form.salePrice) : null,
                saleDate: isSold ? (form.saleDate || null) : null,
                buyer: {
                    name: isSold ? String(form.buyer?.name || '').trim() : '',
                    surname: isSold ? String(form.buyer?.surname || '').trim() : '',
                    email: isSold ? String(form.buyer?.email || '').trim() : '',
                    phone: isSold ? String(form.buyer?.phone || '').trim() : ''
                },
                note: String(form.note || '').trim(),
                diagnostics: form.itemType === 'device'
                    ? (form.diagnostics || []).map((item) => ({
                        item: item.item,
                        itemName: String(item.itemName || '').trim(),
                        answerType: item.answerType,
                        valueStatus: item.answerType === 'status' ? (item.valueStatus || 'untested') : undefined,
                        valueNumber: item.answerType === 'number' ? toNumberOrNull(item.valueNumber) : undefined,
                        valueGrade: item.answerType === 'grade' ? (item.valueGrade || 'C') : undefined
                    }))
                    : [],
                photoIds: currentPhotoAssets.map((item) => item._id).filter(Boolean)
            };

            let saleId = sale?._id;
            if (isCreate) {
                const res = await API.post(API_ENDPOINTS.sales.createFromBuyback(initialBuyback._id), payload);
                saleId = res?.data?._id || null;
            } else if (sale?._id) {
                await API.put(API_ENDPOINTS.sales.update(sale._id), payload);
            }

            if (saleId && pendingPhotoFiles.length) {
                const formData = new FormData();
                pendingPhotoFiles.forEach((entry) => entry?.file && formData.append('photos', entry.file));
                await API.put(API_ENDPOINTS.sales.uploadPhotos(saleId), formData, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
            }

            clearPending(setPendingPhotoFiles);
            await onSaved?.();
            close?.();
        } catch (err) {
            setError(err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={onSubmit} className="space-y-4">
            {isCreate ? (
                <section className="border-2 border-indigo-300 bg-indigo-50 rounded-md p-4 space-y-3">
                    <div className="text-base font-semibold text-indigo-900">Podklady pro nacenění prodeje</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="bg-white border border-indigo-200 rounded px-3 py-2">
                            <div className="text-xs uppercase tracking-wide text-indigo-700">Pořizovací cena</div>
                            <div className="text-sm font-semibold text-gray-900">{formatMoney(purchasePrice)}</div>
                        </div>
                        <div className="bg-white border border-indigo-200 rounded px-3 py-2">
                            <div className="text-xs uppercase tracking-wide text-indigo-700">Cena oprav</div>
                            <div className="text-sm font-semibold text-gray-900">{formatMoney(repairsPrice)}</div>
                        </div>
                        <div className="bg-white border border-indigo-200 rounded px-3 py-2">
                            <div className="text-xs uppercase tracking-wide text-indigo-700">Celkové náklady</div>
                            <div className="text-sm font-semibold text-gray-900">{formatMoney(totalCosts)}</div>
                        </div>
                    </div>
                    <div className="bg-white border border-indigo-200 rounded p-3">
                        <div className="text-sm font-semibold text-gray-900 mb-2">Použité díly v opravě</div>
                        {repairSummaryLoading ? (
                            <div className="text-sm text-gray-600">Načítám opravu…</div>
                        ) : repairPartsSummary.length === 0 ? (
                            <div className="text-sm text-gray-600">Na opravu zatím nejsou evidované žádné použité díly.</div>
                        ) : (
                            <div className="space-y-1">
                                {repairPartsSummary.map((item) => (
                                    <div key={item.partId} className="grid grid-cols-[1fr_auto_auto_auto] gap-3 text-sm">
                                        <div className="font-medium text-gray-900">{item.name}</div>
                                        <div className="text-gray-700">ks: {item.quantity}</div>
                                        <div className="text-gray-700">jedn.: {formatMoney(item.unitCost)}</div>
                                        <div className="font-semibold text-gray-900">celkem: {formatMoney(item.totalCost)}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            ) : null}

            <div className="grid grid-cols-2 gap-4">
                <Forms.Select
                    label="Typ položky"
                    value={ITEM_TYPE_OPTIONS.find((option) => option.value === form.itemType) || ITEM_TYPE_OPTIONS[0]}
                    options={ITEM_TYPE_OPTIONS}
                    onChange={(option) => setField('itemType', option?.value || 'device')}
                    disabled={!isCreate}
                />
                <Forms.Text
                    label="Zařízení"
                    value={`${initialBuyback?.deviceSnapshot?.name || initialBuyback?.device?.name || '-'} ${initialBuyback?.deviceSnapshot?.code ? `(${initialBuyback?.deviceSnapshot?.code})` : ''}`.trim()}
                    onChange={() => {}}
                    disabled
                />
                {form.itemType === 'sparePart' ? (
                    <Forms.Select
                        label="Náhradní díl"
                        value={partOptions.find((option) => String(option.value) === String(form.part)) || null}
                        options={partOptions}
                        onChange={(option) => setField('part', option?.value || null)}
                    />
                ) : null}
                <Forms.Select
                    label="Stav prodeje"
                    value={SALE_STATUS_OPTIONS.find((option) => option.value === form.status) || SALE_STATUS_OPTIONS[0]}
                    options={SALE_STATUS_OPTIONS}
                    onChange={(option) => setField('status', option?.value || 'listed')}
                    disabled={isCreate}
                />
                <Forms.Number label="Vystaveno za" value={form.listedPrice} onChange={(value) => setField('listedPrice', value)}/>
            </div>

            {isSold ? (
                <>
                    <div className="grid grid-cols-2 gap-4">
                        <Forms.Number label="Prodejní cena" value={form.salePrice} onChange={(value) => setField('salePrice', value)}/>
                        <Forms.Date label="Datum prodeje" value={form.saleDate} onChange={(value) => setField('saleDate', value || null)}/>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Forms.Text label="Jméno kupujícího" value={form.buyer.name} onChange={(value) => setField('buyer.name', value)}/>
                        <Forms.Text label="Příjmení kupujícího" value={form.buyer.surname} onChange={(value) => setField('buyer.surname', value)}/>
                        <Forms.Email label="Email kupujícího" value={form.buyer.email} onChange={(value) => setField('buyer.email', value)}/>
                        <Forms.Text label="Telefon kupujícího" value={form.buyer.phone} onChange={(value) => setField('buyer.phone', value)}/>
                    </div>
                </>
            ) : null}

            {form.itemType === 'device' ? (
                <div>
                    <div className="text-sm font-medium mb-2">Funkčnost zařízení (diagnostika)</div>
                    <div className="space-y-2">
                        {(form.diagnostics || []).map((item, index) => (
                            <div key={`${item.item}_${index}`} className="grid grid-cols-[minmax(260px,1fr)_minmax(320px,2fr)] gap-4 items-center border rounded p-3">
                                <div className="text-sm text-gray-900">{item.itemName || '-'}</div>
                                {item.answerType === 'number' ? (
                                    <input
                                        type="number"
                                        value={item.valueNumber ?? ''}
                                        onChange={(event) => updateDiagnostic(index, {valueNumber: event.target.value})}
                                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                                    />
                                ) : null}
                                {item.answerType === 'status' ? (
                                    <div className="flex flex-wrap gap-8">
                                        {DIAG_STATUS_OPTIONS.map((option) => (
                                            <label key={`${item.item}_${option.value}`} className="inline-flex items-center gap-2 text-sm text-gray-900">
                                                <input
                                                    type="radio"
                                                    name={`diag_status_${item.item || index}`}
                                                    value={option.value}
                                                    checked={item.valueStatus === option.value}
                                                    onChange={() => updateDiagnostic(index, {valueStatus: option.value})}
                                                />
                                                <span>{option.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                ) : null}
                                {item.answerType === 'grade' ? (
                                    <div className="flex flex-wrap gap-8">
                                        {DIAG_GRADE_OPTIONS.map((option) => (
                                            <label key={`${item.item}_${option.value}`} className="inline-flex items-center gap-2 text-sm text-gray-900">
                                                <input
                                                    type="radio"
                                                    name={`diag_grade_${item.item || index}`}
                                                    value={option.value}
                                                    checked={item.valueGrade === option.value}
                                                    onChange={() => updateDiagnostic(index, {valueGrade: option.value})}
                                                />
                                                <span>{option.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            <Forms.Textarea label="Poznámka k prodeji" value={form.note} onChange={(value) => setField('note', value)}/>

            <div className="space-y-3">
                <Forms.File
                    label="Přidat fotografie"
                    multiple
                    onChange={async (e) => {
                        const files = Array.from(e?.target?.files || []);
                        const entries = await buildPendingEntries(files);
                        setPendingPhotoFiles((prev) => {
                            prev.forEach((entry) => revokePreview(entry?.previewUrl));
                            return entries;
                        });
                        if (e?.target) e.target.value = '';
                    }}
                />
                <PhotoGallery
                    title="Nové fotografie k nahrání"
                    items={pendingPhotoItems}
                    emptyMessage="Zatím nejsou vybrány nové fotografie."
                    onRemove={(item) => {
                        setPendingPhotoFiles((prev) => {
                            const next = prev.filter((entry) => entry.id !== item.id);
                            revokePreview(item.previewUrl);
                            return next;
                        });
                    }}
                />
                <PhotoGallery
                    title="Aktuální fotografie zařízení/dílu"
                    items={currentPhotoItems}
                    emptyMessage="Zatím nejsou dostupné žádné fotografie."
                    onRemove={sale?._id ? (item) => removeStoredPhoto(item.id) : undefined}
                    removeLabel="Smazat"
                />
            </div>

            {error ? <Error error={error}/> : null}
            <Forms.Button submit label={saving ? 'Ukládám…' : (isCreate ? 'Vytvořit prodej' : 'Uložit')} disabled={saving}/>
        </form>
    );
};

function toNumberOrNull(value) {
    if (value === '' || value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

async function buildPendingEntries(files) {
    const entries = await Promise.all((Array.isArray(files) ? files : []).map(async (file) => {
        const previewUrl = URL.createObjectURL(file);
        const dimensions = await readDimensions(previewUrl);
        return {
            id: `${file.name}_${file.lastModified}_${file.size}_${Math.random().toString(36).slice(2, 8)}`,
            file,
            name: file.name,
            previewUrl,
            width: dimensions?.width || null,
            height: dimensions?.height || null,
            dimensionsLabel: dimensions?.width && dimensions?.height ? `${dimensions.width}×${dimensions.height}` : '',
            sizeLabel: formatSize(file.size)
        };
    }));
    return entries;
}

function readDimensions(src) {
    return new Promise((resolve) => {
        const image = new Image();
        image.onload = () => resolve({width: image.naturalWidth || null, height: image.naturalHeight || null});
        image.onerror = () => resolve({width: null, height: null});
        image.src = src;
    });
}

function formatSize(size) {
    const value = Number(size);
    if (!Number.isFinite(value) || value < 0) return '';
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
    return `${(value / (1024 * 1024)).toFixed(2)} MB`;
}

function revokePreview(url) {
    if (!url) return;
    URL.revokeObjectURL(url);
}

function clearPending(setter) {
    setter((prev) => {
        prev.forEach((entry) => revokePreview(entry?.previewUrl));
        return [];
    });
}

export default SaleModal;

function getPartUnitCost(part) {
    if (!part || typeof part !== 'object') return 0;
    const candidates = [
        Number(part?.averagePurchasePricePerUnit?.gross),
        Number(part?.averagePurchasePricePerUnit?.net),
        Number(part?.expectedPurchasePricePerUnit?.gross),
        Number(part?.expectedPurchasePricePerUnit?.net)
    ];
    const value = candidates.find((item) => Number.isFinite(item) && item >= 0);
    return Number.isFinite(value) ? value : 0;
}

function formatMoney(value) {
    if (value === null || value === undefined || value === '') return '-';
    const number = Number(value);
    if (!Number.isFinite(number)) return '-';
    return `${number.toLocaleString('cs-CZ')} CZK`;
}
